import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsAPI, historyAPI } from '../services/api';

const categories = [
  { id: 'rights', name: 'Rights & Responsibilities', icon: '⚖️' },
  { id: 'aboriginal', name: 'Aboriginal Peoples', icon: '🪶' },
  { id: 'languages', name: 'English & French', icon: '🗣️' },
  { id: 'diversity', name: 'Canadian Diversity', icon: '🌍' },
  { id: 'exploration', name: 'Early Exploration', icon: '⛵' },
  { id: 'struggle', name: 'Struggle for Continent', icon: '⚔️' },
  { id: 'war1812', name: 'War of 1812', icon: '🎖️' },
  { id: 'confederation', name: 'Confederation', icon: '🏛️' },
  { id: 'expansion', name: 'Western Expansion', icon: '🚂' },
  { id: 'wwi', name: 'World War I', icon: '🎗️' },
  { id: 'wwii', name: 'World War II', icon: '✈️' },
  { id: 'modern', name: 'Modern Canada', icon: '🏙️' },
  { id: 'government', name: 'Government System', icon: '🏛️' },
  { id: 'elections', name: 'Elections', icon: '🗳️' },
  { id: 'justice', name: 'Justice System', icon: '⚖️' },
  { id: 'symbols', name: 'Canadian Symbols', icon: '🍁' },
  { id: 'economy', name: 'Economy & Trade', icon: '💼' },
  { id: 'regions', name: 'Regions & Geography', icon: '🗺️' }
];

export default function Stats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [weakQuestions, setWeakQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [statsRes, weakRes] = await Promise.all([
        statsAPI.getUserStats(),
        historyAPI.getWeakQuestions(5)
      ]);

      setStats(statsRes.data);
      setWeakQuestions(weakRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-white text-2xl">Loading stats...</div>
      </div>
    );
  }

  const overallAccuracy = stats?.totalAnswered > 0
    ? Math.round((stats.totalCorrect / stats.totalAnswered) * 100)
    : 0;

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'bg-green-500';
    if (accuracy >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-800">📊 Your Statistics</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
            >
              ← Back
            </button>
          </div>

          {/* Overall Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg p-6 text-white text-center">
              <div className="text-4xl font-bold">{stats?.totalAnswered || 0}</div>
              <div className="text-sm opacity-90">Total Answered</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-lg p-6 text-white text-center">
              <div className="text-4xl font-bold">{stats?.totalCorrect || 0}</div>
              <div className="text-sm opacity-90">Correct</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-700 rounded-lg p-6 text-white text-center">
              <div className="text-4xl font-bold">{stats?.totalWrong || 0}</div>
              <div className="text-sm opacity-90">Wrong</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg p-6 text-white text-center">
              <div className="text-4xl font-bold">{overallAccuracy}%</div>
              <div className="text-sm opacity-90">Accuracy</div>
            </div>
          </div>

          {/* Streak and Score */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card text-center">
              <div className="text-2xl mb-2">🔥</div>
              <div className="text-3xl font-bold text-gray-800">{stats?.currentStreak || 0}</div>
              <div className="text-sm text-gray-600">Current Streak</div>
            </div>
            <div className="card text-center">
              <div className="text-2xl mb-2">⭐</div>
              <div className="text-3xl font-bold text-gray-800">{stats?.bestStreak || 0}</div>
              <div className="text-sm text-gray-600">Best Streak</div>
            </div>
            <div className="card text-center">
              <div className="text-2xl mb-2">🏆</div>
              <div className="text-3xl font-bold text-gray-800">{stats?.totalScore || 0}</div>
              <div className="text-sm text-gray-600">Total Score</div>
            </div>
          </div>

          {/* Per-Topic Breakdown */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Topic Breakdown</h2>
            <div className="space-y-3">
              {categories.map(cat => {
                const topicStats = stats?.byTopic?.[cat.id] || { correct: 0, wrong: 0 };
                const total = topicStats.correct + topicStats.wrong;
                const accuracy = total > 0 ? Math.round((topicStats.correct / total) * 100) : 0;

                return (
                  <div key={cat.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{cat.icon}</span>
                      <span className="font-medium text-gray-800">{cat.name}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-600">
                        <span className="text-green-600 font-bold">{topicStats.correct}</span>
                        {' / '}
                        <span className="text-red-600 font-bold">{topicStats.wrong}</span>
                      </div>
                      {total > 0 && (
                        <span className={`${getAccuracyColor(accuracy)} text-white px-3 py-1 rounded-full text-sm font-bold`}>
                          {accuracy}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Weak Questions */}
          {weakQuestions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">⚠️ Questions to Review</h2>
              <div className="space-y-3">
                {weakQuestions.map((q, idx) => (
                  <div key={idx} className="p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <div className="font-medium text-gray-800 mb-2">{q.question}</div>
                    <div className="text-sm text-gray-600">
                      <span className="text-green-600">{q.timesCorrect || 0} correct</span>
                      {' • '}
                      <span className="text-red-600">{q.timesWrong || 0} wrong</span>
                      {' • '}
                      <span className="font-bold">{q.accuracy || 0}% accuracy</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/quiz?mode=review')}
                  className="btn btn-danger"
                >
                  🔄 Review These Questions
                </button>
              </div>
            </div>
          )}

          {/* Achievements Section */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">🏆 Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`card text-center ${stats?.totalScore >= 100 ? 'bg-yellow-50 border-yellow-500 border-2' : 'opacity-50'}`}>
                <div className="text-4xl mb-2">💯</div>
                <div className="font-bold">Century</div>
                <div className="text-sm text-gray-600">Score 100+ points</div>
              </div>
              <div className={`card text-center ${stats?.bestStreak >= 10 ? 'bg-red-50 border-red-500 border-2' : 'opacity-50'}`}>
                <div className="text-4xl mb-2">🔥</div>
                <div className="font-bold">On Fire</div>
                <div className="text-sm text-gray-600">10 correct in a row</div>
              </div>
              <div className={`card text-center ${stats?.masteredTopics >= 5 ? 'bg-blue-50 border-blue-500 border-2' : 'opacity-50'}`}>
                <div className="text-4xl mb-2">🎓</div>
                <div className="font-bold">Scholar</div>
                <div className="text-sm text-gray-600">Master 5 topics</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
